import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { searchResultsInfo } from '../../../../shared/core/shared.selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { CommonService } from '../../../../shared/services/common.service';
import { SearchService } from '../../core/search.service';
import { ALL_RESULTS_TAB, SEARCH_ENTITIES, SearchEntityConfig } from '../../../../shared/constants/search-entities';

@Component({
    selector: 'app-search-details',
    templateUrl: './search-details.component.html',
    styleUrls: ['./search-details.component.scss'],
    standalone: false
})
export class SearchDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabGroup') tabs: MatTabGroup;
  // Registry entries rendered as tabs: "All" first (cross-category, always
  // present), then legacy tabs (daily report / issues) which are always
  // visible, then entity types that actually have results.
  tabsList: SearchEntityConfig[] = [];
  // Derived from tabsList (tabSlug -> index), not hardcoded.
  tabIndexMap: Record<string, number> = {};
  searchText: any;
  totalSearchCount: any;
  shownSearchCount = 0;
  isTruncated = false;
  selectedTabIndex = 0;
  resultsByType: any = {};
  defaultDateValue = 'All Time';
  private currentTabSlug: string;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private searchService: SearchService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
  ) {

  }

  ngOnInit() {
    this.commonService.closeSearchPopup.next(true);

    this.rebuildTabs();

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.fs === undefined) {
          if (qp.tab) {
            this.currentTabSlug = qp.tab;
            this.selectedTabIndex = this.tabIndexMap[qp.tab] || 0;
          }
        }

        if (qp['searchText'] !== undefined && qp['searchText'].length >= 3) {
          this.searchText = qp['searchText'];
        } else {
          this.resultsByType = {};
          this.rebuildTabs();
        }
      });

    this.store.select(searchResultsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.resultsByType = data?.resultsByType || {};
        this.shownSearchCount = SEARCH_ENTITIES
          .reduce((count, entity) => count + (this.resultsByType?.[entity.searchType]?.length || 0), 0);
        // The api returns a capped window per entity type, so the rows on
        // screen can be fewer than what actually matched. Report the real
        // total and say so when the two differ, instead of quietly presenting
        // the truncated count as the answer.
        this.totalSearchCount = data?.totalRecords ?? this.shownSearchCount;
        this.isTruncated = this.totalSearchCount > this.shownSearchCount;
        this.rebuildTabs();

        if (data?.length > 0) {
          this.commonService.closeSearchPopup.next(true);
        }
      });
  }

  rebuildTabs() {
    this.tabsList = [
      // "All" leads: the best matches regardless of category, so a search
      // answers itself without the user guessing which tab to open.
      ALL_RESULTS_TAB,
      ...SEARCH_ENTITIES.filter(entity =>
        entity.alwaysShowTab || (this.resultsByType?.[entity.searchType]?.length > 0),
      ),
    ];

    this.tabIndexMap = {};
    this.tabsList.forEach((entity, index) => {
      this.tabIndexMap[entity.tabSlug] = index;
    });

    // No explicit tab in the URL ("See all results" lands here) → All, which
    // has results whenever anything matched.
    this.selectedTabIndex = this.currentTabSlug
      ? this.tabIndexMap[this.currentTabSlug] || 0
      : 0;
  }

  ngAfterViewInit() {
    this.tabs._handleClick = this.onTabChange?.bind(this);
  }

  onTabChange(tab) {
    this.searchService.closeRightPanel.next();
    this.router.navigate([], {
      queryParams: {
        tab: tab.textLabel.toLowerCase().replaceAll(' ', '-'),
        selectedId: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
